export type FocusStrategy =
  | { kind: 'no-focus-capability' }
  | { kind: 'already-continuous' }
  | { kind: 'continuous' }
  | { kind: 'manual' }
  | { kind: 'manual-despite-settings' }
  | { kind: 'unsupported-mode' };

/*
  Android drivers often report a current focusMode of 'continuous' while advertising 'manual' as the
  only mode they support. Which of the two readings is honest is unsettled — getCameraOptions asks
  for continuous at getUserMedia time, so the setting may be true and the capability list merely
  half-read — so that contradiction gets its own kind and the caller only counts it.
*/
const getFocusStrategy = (capabilities: MediaTrackCapabilities, settings: MediaTrackSettings): FocusStrategy => {
  if (!('focusMode' in capabilities)) {
    return { kind: 'no-focus-capability' };
  }

  const focusModes = capabilities.focusMode;
  const currentFocusMode = (settings as MediaTrackSettings & { focusMode?: string }).focusMode;
  const claimsContinuous = currentFocusMode === 'continuous';

  if (Array.isArray(focusModes) && focusModes.includes('continuous')) {
    return claimsContinuous ? { kind: 'already-continuous' } : { kind: 'continuous' };
  }

  if ('focusDistance' in capabilities) {
    return claimsContinuous ? { kind: 'manual-despite-settings' } : { kind: 'manual' };
  }

  return { kind: 'unsupported-mode' };
};

export type FocusRetryReason = 'absent' | 'contradiction';

/*
  A read with no focusMode at all, or one whose setting contradicts its capabilities, is shaped
  like the half-read Android Chrome produces before the camera delivers frames — worth re-reading
  before committing a verdict. The reason is recorded because 'absent' fires on every platform
  that never exposes focusMode (all of iOS), and would otherwise drown out the contradictions
  the retry exists to resolve.
*/
export const getFocusRetryReason = (
  capabilities: MediaTrackCapabilities,
  settings: MediaTrackSettings,
): FocusRetryReason | undefined => {
  const { kind } = getFocusStrategy(capabilities, settings);
  if (kind === 'no-focus-capability') {
    return 'absent';
  }
  if (kind === 'manual-despite-settings') {
    return 'contradiction';
  }
  return undefined;
};

export const readTrackCapabilities = (track: MediaStreamTrack): MediaTrackCapabilities => {
  try {
    // Not implemented everywhere (older Firefox), and some UAs throw instead of omitting it.
    return typeof track.getCapabilities === 'function' ? track.getCapabilities() : {};
  } catch {
    return {};
  }
};

export type FocusReadHandlers = {
  onRetry: (reason: FocusRetryReason) => void;
  onSettled: (capabilities: MediaTrackCapabilities, settings: MediaTrackSettings) => void;
};

export const FOCUS_REREAD_DELAY_MS = 1000;

/*
  Android Chrome can report a partial capability set until the camera is delivering frames. A
  missing focusMode and one that contradicts the current setting are both shaped like that
  half-read, so re-read once before committing a verdict for the track.
*/
export const readFocusStateWithRetry = (
  track: MediaStreamTrack,
  { onRetry, onSettled }: FocusReadHandlers,
): (() => void) => {
  let retryTimer: ReturnType<typeof setTimeout> | undefined;
  const attempt = (retriesLeft: number) => {
    const capabilities = readTrackCapabilities(track);
    const settings = track.getSettings();
    const retryReason = getFocusRetryReason(capabilities, settings);
    if (retryReason && retriesLeft > 0 && track.readyState === 'live') {
      onRetry(retryReason);
      retryTimer = setTimeout(() => attempt(retriesLeft - 1), FOCUS_REREAD_DELAY_MS);
      return;
    }
    onSettled(capabilities, settings);
  };
  attempt(1);
  return () => clearTimeout(retryTimer);
};

export default getFocusStrategy;
